import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DashboardPanel from './DashboardPanel';
import { gridCellsFor } from '../../utils/gridCells';

export default function DashboardGridLayout({ config, darkMode, onLayoutChange, editMode, onPanelClick, selectedPanelId }) {
  const panels = config?.panels ?? [];
  const gridLayout = config?.gridLayout || null;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    const observer = typeof ResizeObserver !== 'undefined' && containerRef.current
      ? new ResizeObserver(updateWidth)
      : null;
    if (observer) observer.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (observer) observer.disconnect();
    };
  }, []);

  const [currentLayout, setCurrentLayout] = useState(gridLayout);

  useEffect(() => {
    if (gridLayout && Array.isArray(gridLayout)) {
      setCurrentLayout(gridLayout);
    }
  }, [gridLayout]);

  const layout = useMemo(() => {
    if (currentLayout && Array.isArray(currentLayout) && currentLayout.length > 0) {
      return gridCellsFor(panels, currentLayout);
    }
    const defaultLayout = panels
      .filter((p) => p.type === 'component' || p.type === 'chat')
      .map((panel) => {
        const isCenter = panel.position === 'center';
        const isLeft = panel.position === 'left';
        const isRight = panel.position === 'right';
        let x = 0;
        let w = 4;
        let h = 12;
        if (isCenter) {
          x = 4;
          w = 4;
        } else if (isRight) {
          x = 8;
          w = 4;
        }
        return {
          i: panel.id,
          x,
          y: 0,
          w,
          h,
          minW: 2,
          minH: 4,
          maxW: 12,
          maxH: 20,
        };
      });
    return defaultLayout;
  }, [panels, currentLayout]);

  const handleLayoutChange = useCallback((newLayout) => {
    setCurrentLayout(newLayout);
    if (onLayoutChange) {
      onLayoutChange(newLayout);
    }
  }, [onLayoutChange]);

  return (
    <Box ref={containerRef} sx={{ height: '100%', minHeight: 0, width: '100%', position: 'relative', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
      <GridLayout
        className="layout"
        layout={layout}
        gridConfig={{ cols: 12, rowHeight: 30 }}
        width={containerWidth || 1200}
        onLayoutChange={handleLayoutChange}
        dragConfig={{ enabled: !!editMode, handle: '.drag-handle' }}
        resizeConfig={{ enabled: !!editMode }}
        style={{ minHeight: '100%' }}
      >
        {panels
          .filter((p) => p.type === 'component' || p.type === 'chat')
          .map((panel) => (
            <Box
              key={panel.id}
              sx={{
                height: '100%',
                width: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                '& > .MuiPaper-root': { flex: 1 },
                ...(editMode && onPanelClick && {
                  cursor: 'pointer',
                  outline: selectedPanelId === panel.id ? '2px solid' : '1px solid transparent',
                  outlineColor: 'primary.main',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }),
              }}
              onClick={editMode && onPanelClick ? () => onPanelClick(panel) : undefined}
            >
              {editMode && (
                <Box
                  className="drag-handle"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    right: 4,
                    height: 24,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    cursor: 'move',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.7,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  ⋮⋮
                </Box>
              )}
              <DashboardPanel panel={panel} darkMode={darkMode} />
            </Box>
          ))}
      </GridLayout>
    </Box>
  );
}
